import { useEffect, useState, useCallback, useMemo } from 'react';
import type { ReactElement } from 'react';

import { ApiClient, ApiError, PaginatedResponse } from '@/api';
import { Message, StampAction } from '@/entities';

import { useCSPRClick } from './use-cspr-click';

export type ApiRequestState<T> = {
  loading: boolean;
  response: T | null;
  error: {
    code: string;
    message: string;
    description?: string | ReactElement;
  } | null;
};

export type ApiRequestResult<T> = ApiRequestState<T> & { refetch: () => void };

export const useApi = () => {
  const { clickRef, csprClickReady } = useCSPRClick();

  const apiClient = useMemo(
    () => (clickRef ? new ApiClient(clickRef) : null),
    [clickRef?.digestAuth]
  );

  return { apiClient, apiReady: csprClickReady };
};

const useMakeRequest = <T, A extends readonly unknown[]>(
  methodSelector: (client: ApiClient) => (...args: A) => Promise<T>,
  ...args: A
): ApiRequestResult<T> => {
  const [state, setState] = useState<ApiRequestState<T>>({
    loading: false,
    response: null,
    error: null
  });

  const { apiClient, apiReady } = useApi();

  const doRequest = useCallback(() => {
    if (!apiClient) {
      return;
    }

    let canceled = false;

    (async () => {
      setState((prev) => ({
        loading: true,
        response: prev.response,
        error: null
      }));

      try {
        const response = await methodSelector(apiClient).bind(apiClient)(...args);
        if (canceled) {
          return;
        }

        setState({
          response,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (canceled) {
          return;
        }

        console.log(err);
        setState({
          loading: false,
          response: null,
          error: {
            message: err instanceof Error ? err.message : String(err),
            code: err instanceof ApiError ? err.code : '500',
            description: err instanceof ApiError ? err.description : '500',
          }
        });
      }
    })();

    return () => {
      canceled = true;
    };
  }, [apiClient, methodSelector, ...args]);

  useEffect(() => {
    doRequest();
  }, [doRequest]);

  return { ...state, refetch: doRequest };
}

export const useGetAllMessages = (
  page: number = 1,
  perPage: number = 10
): ApiRequestResult<PaginatedResponse<Message>> => {
  const selectGetAllMessages = useCallback(
    (client: ApiClient) => client.getAllMessages,
    []
  );

  return useMakeRequest(selectGetAllMessages, page, perPage);
};

export const useGetMessagesReceivedByAccount = (
  publicKey?: string,
  page: number = 1,
  perPage: number = 10
): ApiRequestResult<PaginatedResponse<Message>> => {
  const selectGetMessagesReceivedBy = useCallback(
    (client: ApiClient) => client.getMessagesReceivedBy,
    []
  );

  return useMakeRequest(selectGetMessagesReceivedBy, publicKey, page, perPage);
};

export const useGetMessagesSentByAccount = (
  publicKey?: string,
  page: number = 1,
  perPage: number = 10
): ApiRequestResult<PaginatedResponse<Message>> => {
  const selectGetMessagesSentBy = useCallback(
    (client: ApiClient) => client.getMessagesSentBy,
    []
  );

  return useMakeRequest(selectGetMessagesSentBy, publicKey, page, perPage);
};

export const useGetMessage = (id?: number,): ApiRequestResult<Message|null> => {
  const selectGetMessage = useCallback(
    (client: ApiClient) => client.getMessage,
    []
  );

  return useMakeRequest(selectGetMessage, id);
};

export const useGetStampHistoryForAccount = (
  publicKey: string,
  page: number = 1,
  perPage: number = 10
): ApiRequestResult<PaginatedResponse<StampAction>> => {
  const selectGetStampsHistoryFor = useCallback(
    (client: ApiClient) => client.getStampHistoryFor,
    []
  );

  return useMakeRequest(selectGetStampsHistoryFor, publicKey, page, perPage);
};

export const useGetStampBalanceForAccount = (
  publicKey?: string
): ApiRequestResult<number> => {
  const selectGetStampsHistoryFor = useCallback(
    (client: ApiClient) => client.getStampBalanceFor,
    []
  );

  return useMakeRequest(selectGetStampsHistoryFor, publicKey);
};
